import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useRangeKey } from '../TimeRangeControl';
import { fetchCve } from '../api';
import Leaderboard, { type LeaderboardColumn } from '../charts/Leaderboard';
import type { CvePayload } from '../types';
import { entityHref } from '../util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';
import { Link } from 'react-router-dom';
import ExpandableChipRow from '../../../components/ExpandableChipRow';

type CveRow = CvePayload['rows'][number];
type SortKey = 'first_mention' | 'adoption_lag_days' | 'disclosure_year';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'first_mention', label: 'Most recently mentioned' },
  { value: 'adoption_lag_days', label: 'Largest adoption lag' },
  { value: 'disclosure_year', label: 'Newest CVE' },
];

function sortRows(rows: CveRow[], key: SortKey): CveRow[] {
  const copy = [...rows];
  if (key === 'first_mention') {
    copy.sort((a, b) => (b.first_mention || '').localeCompare(a.first_mention || ''));
  } else if (key === 'disclosure_year') {
    copy.sort((a, b) => (b.disclosure_year ?? 0) - (a.disclosure_year ?? 0));
  } else if (key === 'adoption_lag_days') {
    copy.sort(
      (a, b) =>
        (b.adoption_lag_days ?? -1) - (a.adoption_lag_days ?? -1),
    );
  }
  return copy;
}

const columns = (): LeaderboardColumn<CveRow>[] => [
  {
    key: 'cve',
    label: 'CVE',
    width: '140px',
    render: (row) => <span className="trends-cve-id">{row.cve_id}</span>,
  },
  {
    key: 'disclosure',
    label: 'Disclosed',
    align: 'right',
    width: '90px',
    render: (row) => row.disclosure_year ?? '—',
  },
  {
    key: 'first_mention',
    label: 'First mention',
    width: '110px',
    render: (row) => row.first_mention ?? '—',
  },
  {
    key: 'first_adoption',
    label: 'First adoption',
    width: '110px',
    render: (row) => row.first_adoption ?? '—',
  },
  {
    key: 'lag',
    label: 'Lag',
    align: 'right',
    width: '70px',
    render: (row) =>
      row.adoption_lag_days == null ? '—' : `${row.adoption_lag_days} d`,
  },
  {
    key: 'families',
    label: 'Adopted by',
    render: (row) => {
      if (row.families.length === 0) return <span className="trends-muted">—</span>;
      const chips = row.families.map((f) => {
        const href = entityHref('MalwareFamily', f);
        return href ? (
          <Link key={f} to={href} className="trends-chip">
            {f}
          </Link>
        ) : (
          <span key={f} className="trends-chip">
            {f}
          </span>
        );
      });
      return (
        <ExpandableChipRow
          items={chips}
          wrapperClassName="trends-family-chips"
          toggleClassName="trends-chip trends-chip-toggle"
        />
      );
    },
  },
];

export default function CveTab() {
  const range = useRangeKey();
  const [sortKey, setSortKey] = useState<SortKey>('first_mention');
  const q = useQuery({
    queryKey: ['trends', 'cve', range],
    queryFn: () => fetchCve(range),
    ...QUERY_OPTIONS,
  });

  const rows = useMemo(
    () => (q.data ? sortRows(q.data.rows, sortKey) : []),
    [q.data, sortKey],
  );
  const cols = useMemo(() => columns(), []);

  return (
    <section className="trends-tab-content trends-cve">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="No CVE rows for this range. The CVE section is built by the trends_snapshot Dagster asset from MENTIONS edges."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div className="trends-controls-row">
              <h2 className="trends-section-title">
                {rows.length} CVEs in range
              </h2>
              <label className="trends-sort-control">
                <span>Sort</span>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Leaderboard rows={rows} columns={cols} rowKey={(row) => row.cve_id} />
          </>
        )}
      </TabStatus>
    </section>
  );
}
